import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobSearchBar,
  KoobSegmentedControl,
  KoobSegmentedControlItem,
} from '@koob/ui';
import { FOLDER_ROWS_ALL, FolderRowTypeEnum } from '@koob/utils';
import FolderBookingStatusFilter from './FolderBookingStatusFilter';

const TYPES = [
  FOLDER_ROWS_ALL,
  FolderRowTypeEnum.TRIP,
  FolderRowTypeEnum.HOTEL,
  FolderRowTypeEnum.EXPERIENCE,
];

export interface FolderBookingsFiltersProps {
  type: string;
  onTypeChange: (type: string) => void;
  /** Rows per type, from buildFolderBookingList. */
  counts: Record<string, number>;
  query: string;
  onQueryChange: (query: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
}

/**
 * KD-3842 bookings list filters: type (with counts), search on the service,
 * city and provider, and the status in two groups (trips, then hotels and
 * experiences).
 */
export default function FolderBookingsFilters({
  type,
  onTypeChange,
  counts,
  query,
  onQueryChange,
  status,
  onStatusChange,
}: FolderBookingsFiltersProps) {
  const { t } = useTranslation('folders');

  return (
    <div className="flex flex-1 flex-wrap items-center gap-3">
      <KoobSegmentedControl value={type} onValueChange={onTypeChange}>
        {TYPES.map((value) => (
          <KoobSegmentedControlItem key={value} value={value}>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              {t(`bookingsList.types.${value}`)}
              <span className="text-xs text-gray-500">
                {counts[value] ?? 0}
              </span>
            </span>
          </KoobSegmentedControlItem>
        ))}
      </KoobSegmentedControl>

      <div className="ml-auto w-56">
        <KoobSearchBar
          value={query}
          onChange={onQueryChange}
          placeholder={t('bookingsList.searchPlaceholder')}
        />
      </div>

      <div className="w-48">
        <FolderBookingStatusFilter
          status={status}
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
}
